import './styles.css';
import {
  clearBootAttempts,
  isCrashLoop,
  recordBootAttempt,
  repairApplication,
} from './app/recovery';
import { decryptText, encryptText, unlockKey } from './auth/crypto';
import type { MainToWorkerMessage, WorkerToMainMessage } from './shell/protocol';
import { linuxUserName } from './system/users';
import { Accounts, WebStorageAdapter, type AccountRecord } from './storage/accounts';
import { createBackup, parseBackup } from './storage/backup';
import { IndexedDbBlobStore } from './storage/idb';
import type { SerializedShell } from './storage/serialize';
import { createTerminal } from './terminal/terminal';
import { setupAdmin } from './ui/admin';
import { LoginScreen } from './ui/login';
import { setupPedagogyPanel } from './ui/pedagogy';
import { allCheatSheetsMarkdown } from './pedagogy/cheatsheets';
import { promptUser, showInfo } from './ui/prompt';
import { applyTheme, getStoredFontSize, getStoredTheme } from './ui/theme';
import { THEMES } from './ui/themes';
import { applyTranslations, setLocale } from './ui/i18n';
import { setupUi } from './ui/ui';

recordBootAttempt();
if (isCrashLoop()) {
  // L'application a planté plusieurs fois au démarrage : réparation automatique
  repairApplication();
}

function byId<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (!element) throw new Error(`Élément #${id} introuvable`);
  return element as T;
}

const container = document.getElementById('terminal');
if (!container) throw new Error('Élément #terminal introuvable');

const themeName = getStoredTheme();
applyTheme(themeName);

const worker = new Worker(new URL('./shell/worker.ts', import.meta.url), { type: 'module' });
const send = (message: MainToWorkerMessage): void => worker.postMessage(message);

const terminal = createTerminal(
  container,
  (data) => send({ type: 'input', data }),
  THEMES[themeName],
  getStoredFontSize(),
  (cols, rows) => send({ type: 'resize', cols, rows }),
);

const pedagogyPanel = setupPedagogyPanel({
  send,
  onToggle: () => terminal.fit(),
});
applyTranslations();

const statusUser = document.getElementById('status-user');
const statusCwd = document.getElementById('status-cwd');
const statusAccount = document.getElementById('status-account');

const accounts = new Accounts(new WebStorageAdapter(), new IndexedDbBlobStore());

let currentRecord: AccountRecord | null = null;
let currentKey: CryptoKey | null = null;
let saveTimer: number | null = null;
let snapshotCounter = 0;
const pendingSnapshots = new Map<number, (state: SerializedShell) => void>();

// --- Cycle de session ---

function updateAccountLabel(): void {
  if (statusAccount) {
    statusAccount.textContent = currentRecord
      ? `compte : ${currentRecord.id}`
      : 'invité — non persistant';
  }
}

async function startSession(
  record: AccountRecord | null,
  key: CryptoKey | null,
  state: SerializedShell | null,
): Promise<void> {
  currentRecord = record;
  currentKey = key;
  updateAccountLabel();
  clearBootAttempts();
  // L'utilisateur Linux suit le compte web (invité → « eleve »).
  send({ type: 'session', user: record ? linuxUserName(record.id) : 'eleve' });
  if (state) send({ type: 'restore', state });
  send({ type: 'init' });
  login.hide();
  terminal.focus();
}

const login = new LoginScreen({
  hasAccounts: () => accounts.list().length > 0,

  onLogin: async (id, password) => {
    login.setBusy(true);
    try {
      const result = await accounts.login(id, password);
      if (!result) {
        login.showError('Identifiant ou mot de passe incorrect.');
        return;
      }
      let state: SerializedShell | null = null;
      const blob = await accounts.getBlob(result.record.id);
      if (blob) {
        try {
          state = JSON.parse(await decryptText(result.key, blob)) as SerializedShell;
        } catch {
          login.showError('Données du compte illisibles : la session repart de zéro.');
        }
      }
      await startSession(result.record, result.key, state);
    } catch (error) {
      login.showError(error instanceof Error ? error.message : String(error));
    } finally {
      login.setBusy(false);
    }
  },

  onCreate: async (id, password, acknowledged) => {
    if (!acknowledged) {
      login.showError('Veuillez confirmer que vous acceptez l’absence de récupération.');
      return;
    }
    login.setBusy(true);
    try {
      const { record, key } = await accounts.create(id, password);
      await startSession(record, key, null);
      scheduleSave();
    } catch (error) {
      login.showError(error instanceof Error ? error.message : String(error));
    } finally {
      login.setBusy(false);
    }
  },

  onGuest: () => {
    void startSession(null, null, null);
  },

  onUnlock: async (password) => {
    if (!currentRecord) return;
    login.setBusy(true);
    try {
      const key = await unlockKey(
        password,
        currentRecord.salt,
        currentRecord.iterations,
        currentRecord.verifier,
      );
      if (!key) {
        login.showError('Mot de passe incorrect.');
        return;
      }
      currentKey = key;
      login.hide();
      terminal.focus();
    } finally {
      login.setBusy(false);
    }
  },
});

// --- Sauvegarde chiffrée ---

function scheduleSave(): void {
  if (!currentRecord || !currentKey) return;
  if (saveTimer !== null) window.clearTimeout(saveTimer);
  saveTimer = window.setTimeout(() => {
    saveTimer = null;
    void requestSnapshot().then((state) => saveState(state));
  }, 1200);
}

async function saveState(state: SerializedShell): Promise<void> {
  if (!currentRecord || !currentKey) return;
  try {
    const blob = await encryptText(currentKey, JSON.stringify(state));
    await accounts.putBlob(currentRecord.id, blob);
  } catch {
    /* écriture impossible : la session continue en mémoire */
  }
}

function requestSnapshot(): Promise<SerializedShell> {
  const requestId = ++snapshotCounter;
  return new Promise((resolve) => {
    pendingSnapshots.set(requestId, resolve);
    send({ type: 'snapshot', requestId });
  });
}

worker.addEventListener('message', (event: MessageEvent<WorkerToMainMessage>) => {
  const message = event.data;
  switch (message.type) {
    case 'output':
      terminal.term.write(message.data);
      break;
    case 'clear':
      terminal.term.clear();
      break;
    case 'status':
      if (statusUser) statusUser.textContent = message.user;
      if (statusCwd) statusCwd.textContent = message.cwd;
      break;
    case 'state-changed':
      scheduleSave();
      break;
    case 'pedagogy':
      pedagogyPanel.update(message.state);
      break;
    case 'locale':
      setLocale(message.value);
      break;
    case 'snapshot': {
      const resolve = pendingSnapshots.get(message.requestId);
      if (resolve) {
        pendingSnapshots.delete(message.requestId);
        resolve(message.state);
      }
      break;
    }
  }
});

// Sauvegarde de secours quand l'onglet passe en arrière-plan
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden' && currentRecord && currentKey) {
    void requestSnapshot().then((state) => saveState(state));
  }
});

// --- Export / import de sauvegarde chiffrée ---

function downloadFile(name: string, content: string): void {
  const blob = new Blob([content], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  URL.revokeObjectURL(url);
}

async function exportBackup(): Promise<void> {
  if (!currentRecord || !currentKey) {
    await showInfo(
      'Export',
      'Connectez-vous avec un compte pour exporter une sauvegarde chiffrée.',
    );
    return;
  }
  const state = await requestSnapshot();
  const blob = await encryptText(currentKey, JSON.stringify(state));
  downloadFile(`${currentRecord.id}.nsi-backup`, createBackup(currentRecord, blob));
}

const importInput = byId<HTMLInputElement>('import-file');
importInput.addEventListener('change', () => {
  const file = importInput.files?.[0];
  importInput.value = '';
  if (!file) return;
  void (async () => {
    const parsed = parseBackup(await file.text());
    if (!parsed) {
      await showInfo('Import', 'Fichier .nsi-backup invalide ou version inconnue.');
      return;
    }

    if (accounts.exists(parsed.account.id)) {
      const answer = await promptUser({
        title: 'Remplacer la sauvegarde existante',
        message: `Le compte « ${parsed.account.id} » existe déjà. Saisissez son mot de passe pour remplacer ses données.`,
        fields: [{ id: 'password', label: 'Mot de passe', type: 'password' }],
        submitLabel: 'Remplacer',
      });
      if (!answer) return;
      const record = accounts.list().find((entry) => entry.id === parsed.account.id);
      if (!record) return;
      const key = await unlockKey(
        answer.password ?? '',
        record.salt,
        record.iterations,
        record.verifier,
      );
      if (!key) {
        await showInfo('Import', 'Mot de passe incorrect.');
        return;
      }
      await accounts.putBlob(record.id, parsed.blob);
      await showInfo('Import', 'Sauvegarde remplacée. Connectez-vous pour la retrouver.');
      return;
    }

    await accounts.importRecord(parsed.account, parsed.blob);
    await showInfo(
      'Import',
      `Compte « ${parsed.account.id} » importé. Connectez-vous avec son mot de passe.`,
    );
  })();
});

// --- Réinitialisation et suppression ---

async function resetMachine(): Promise<void> {
  if (!currentRecord || !currentKey) {
    await showInfo('Réinitialiser', 'Connectez-vous avec un compte pour réinitialiser la machine.');
    return;
  }
  const answer = await promptUser({
    title: 'Réinitialiser la machine Debian',
    message:
      'Toutes les données de la machine (fichiers, comptes Linux…) seront remplacées par une image neuve. Le compte web et son mot de passe sont conservés.',
    fields: [{ id: 'password', label: 'Mot de passe du compte', type: 'password' }],
    submitLabel: 'Réinitialiser',
  });
  if (!answer) return;
  const key = await unlockKey(
    answer.password ?? '',
    currentRecord.salt,
    currentRecord.iterations,
    currentRecord.verifier,
  );
  if (!key) {
    await showInfo('Réinitialiser', 'Mot de passe incorrect.');
    return;
  }
  send({ type: 'reset' });
  scheduleSave();
}

async function deleteAccount(): Promise<void> {
  if (!currentRecord || !currentKey) {
    await showInfo('Supprimer', 'Connectez-vous avec un compte pour le supprimer.');
    return;
  }
  const answer = await promptUser({
    title: 'Supprimer mon compte',
    message: `Toutes les données de « ${currentRecord.id} » seront définitivement supprimées. Saisissez le mot de passe et l'identifiant pour confirmer.`,
    fields: [
      { id: 'password', label: 'Mot de passe', type: 'password' },
      { id: 'id', label: `Identifiant (${currentRecord.id})` },
    ],
    submitLabel: 'Supprimer définitivement',
  });
  if (!answer) return;
  if (answer.id.trim().toLowerCase() !== currentRecord.id) {
    await showInfo('Supprimer', "L'identifiant saisi ne correspond pas.");
    return;
  }
  const key = await unlockKey(
    answer.password ?? '',
    currentRecord.salt,
    currentRecord.iterations,
    currentRecord.verifier,
  );
  if (!key) {
    await showInfo('Supprimer', 'Mot de passe incorrect.');
    return;
  }
  await accounts.remove(currentRecord.id);
  window.location.reload();
}

// --- Menu : session, sauvegardes, réparation ---

setupUi({
  terminal,
  send,
  onLock: () => {
    if (!currentRecord) return;
    currentKey = null;
    login.showLock(currentRecord.id);
  },
  onLogout: () => {
    void (async () => {
      if (currentRecord && currentKey) {
        const state = await requestSnapshot();
        await saveState(state);
      }
      window.location.reload();
    })();
  },
  onExportBackup: () => void exportBackup(),
  onImportBackup: () => importInput.click(),
  onResetMachine: () => void resetMachine(),
  onDeleteAccount: () => void deleteAccount(),
  onDownloadCheatsheets: () => downloadFile('fiches-memo-nsi.md', allCheatSheetsMarkdown()),
});

byId('login-repair').addEventListener('click', () => {
  void (async () => {
    const confirmed = await promptUser({
      title: "Réparer l'application",
      message:
        "Les caches, le service worker et les préférences d'affichage seront vidés, puis la page rechargée. Aucun compte ne sera touché.",
      submitLabel: 'Réparer',
    });
    if (confirmed) repairApplication();
  })();
});

const openAdmin = setupAdmin({ accounts });
byId('admin-link').addEventListener('click', openAdmin);
byId('login-import').addEventListener('click', () => importInput.click());

// --- Démarrage : écran de connexion ---

login.showLogin();
updateAccountLabel();
